import { z } from "zod";
import { AgentId, idSchema, LocalizedText, TenantId, TemplateId, Timestamp, UserId } from "./common.js";
import { CapabilityId, CapabilityRef, McpScope, PermissionMode } from "./entities.js";

/**
 * 能力包（Capability）：agent 能力的可复用单元（doc/capabilities.md）。
 *   身份模板 = 人设（提示词 / 模型 / 资源）+ 引用若干能力包（固定版本）
 *   agent 实例 = 模板的能力包 ± 实例级增减
 * 能力包带：完整 skill 目录（SKILL.md + 脚本 + 参考资料）、工具（平台 MCP 分组 / 外部 MCP 服务 / 预授权工具）、
 * 运行环境要求（镜像、出网域名）、可配置参数。
 */

export const SecretId = idSchema("secret");
export type SecretId = z.infer<typeof SecretId>;
export const McpServerId = idSchema("mcpServer");
export type McpServerId = z.infer<typeof McpServerId>;

/** skill 目录里的一个文件；path 相对 skill 根，如 SKILL.md、scripts/slice.py、reference/styles.md */
export const CapabilityFile = z.object({
  path: z
    .string()
    .min(1)
    .max(256)
    .refine((p) => !p.startsWith("/") && !p.split("/").includes(".."), "invalid path"),
  /** utf8 文本；二进制用 base64 */
  content: z.string().max(1_000_000),
  encoding: z.enum(["utf8", "base64"]).default("utf8"),
  executable: z.boolean().default(false),
});
export type CapabilityFile = z.infer<typeof CapabilityFile>;

export const CapabilitySkill = z.object({
  /** skill 目录名（.claude/skills/<name>/） */
  name: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
  /** 必须包含 SKILL.md；SKILL.md 内可用 <!-- lang:zh-CN --> / <!-- lang:en --> 分段按项目语言渲染 */
  files: z.array(CapabilityFile).min(1).max(200),
});
export type CapabilitySkill = z.infer<typeof CapabilitySkill>;

/** 外部 MCP 服务引用：由租户在"MCP 服务"里登记（凭据平台代管），能力包按 id 或按 slug 引用 */
export const CapabilityMcpRef = z.object({
  /** 在 agent 的 mcp.json 里的服务名（工具名前缀 mcp__<name>__） */
  name: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/),
  /** 租户登记的外部 MCP 服务 */
  serverId: McpServerId,
  /** 预授权的工具（不在列表里的工具仍按权限策略询问）；"*" 表示全部 */
  allowTools: z.array(z.string()).default([]),
});

export const CapabilityParam = z.object({
  key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,31}$/),
  label: LocalizedText,
  type: z.enum(["string", "number", "boolean", "enum"]),
  options: z.array(z.string()).optional(),
  default: z.union([z.string(), z.number(), z.boolean()]).optional(),
});

export const CapabilityManifest = z.object({
  name: LocalizedText,
  description: LocalizedText,
  /** 用于分类 / 推荐：art / code / test / planning / orchestration / ops … */
  category: z.string().max(32),
  icon: z.string().max(32).default("sparkles"),
  skills: z.array(CapabilitySkill).default([]),
  tools: z
    .object({
      /** 需要的平台 MCP 分组（与模板的 mcpScopes 取并集） */
      mcpScopes: z.array(McpScope).default([]),
      /** Claude Code 内置工具预授权（Bash / Edit / WebFetch …），与模板 allowedTools 取并集 */
      allowedTools: z.array(z.string()).default([]),
      /** 权限规则（追加在模板规则之后） */
      permissionRules: z.array(z.object({ tool: z.string(), pattern: z.string().optional(), mode: PermissionMode })).default([]),
      externalMcp: z.array(CapabilityMcpRef).default([]),
    })
    .default({ mcpScopes: [], allowedTools: [], permissionRules: [], externalMcp: [] }),
  runtime: z
    .object({
      /** 需要的镜像（例如 agentsaas/agent-art:latest）；多个能力包要求不同镜像时以模板为准，冲突报 capability.image_conflict */
      image: z.string().nullable().default(null),
      /** 需要额外放行的出网域名（host 或 host:port），合并进该 agent 的 egress 白名单 */
      egress: z.array(z.string().max(255)).default([]),
    })
    .default({ image: null, egress: [] }),
  params: z.array(CapabilityParam).default([]),
  /** 追加到 CLAUDE.md 的一段说明（告诉 agent 这个能力包怎么用），支持语言分段 */
  instructions: z.string().max(20_000).default(""),
});
export type CapabilityManifest = z.infer<typeof CapabilityManifest>;

export const Capability = z.object({
  id: CapabilityId,
  /** null = 平台内置 */
  tenantId: TenantId.nullable(),
  version: z.number().int().positive(),
  manifest: CapabilityManifest,
  status: z.enum(["active", "deprecated"]).default("active"),
  /** manifest 总大小（字节），列表展示用 */
  sizeBytes: z.number().int().nonnegative().optional(),
  createdBy: UserId.nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Capability = z.infer<typeof Capability>;

/** 解析后的有效能力（agent 详情展示用） */
export const EffectiveCapabilities = z.object({
  agentId: AgentId,
  items: z.array(z.object({ id: CapabilityId, version: z.number().int(), name: LocalizedText, source: z.enum(["template", "agent"]) })),
  mcpScopes: z.array(McpScope),
  allowedTools: z.array(z.string()),
  externalMcp: z.array(z.object({ name: z.string(), serverId: McpServerId })),
  image: z.string(),
  egress: z.array(z.string()),
  skills: z.array(z.string()),
  /** 解析告警（被跳过的缺失能力包、重名覆盖等）；前端按 code 翻译 */
  warnings: z
    .array(z.object({ code: z.enum(["capability.missing", "capability.skill_overridden", "capability.mcp_overridden", "capability.image_conflict"]), params: z.record(z.string(), z.string()) }))
    .optional(),
});

// ───────────── 租户密钥（平台代管，值永不回传） ─────────────

export const Secret = z.object({
  id: SecretId,
  tenantId: TenantId,
  name: z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/),
  description: z.string().max(500).default(""),
  /** 形如 "sk-…abcd" */
  hint: z.string().nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Secret = z.infer<typeof Secret>;

// ───────────── 外部 MCP 服务（经平台 mcp-proxy 代理，凭据注入） ─────────────

export const McpServer = z.object({
  id: McpServerId,
  tenantId: TenantId,
  name: z.string().min(1).max(64),
  /** 远端 Streamable HTTP 端点 */
  url: z.url(),
  /** 注入的请求头：值里用 {{secret:NAME}} 引用租户密钥，如 "Bearer {{secret:ACME_KEY}}" */
  headers: z.record(z.string(), z.string()).default({}),
  status: z.enum(["unverified", "ok", "error"]),
  /** 最近一次探测得到的工具列表 */
  tools: z.array(z.object({ name: z.string(), description: z.string().optional() })).default([]),
  lastError: z.string().nullable(),
  createdAt: Timestamp,
});
export type McpServer = z.infer<typeof McpServer>;

// ───────────── 租户自带媒体供应商 key（两种来源都支持：平台 key 计费 / 租户 key 只计量） ─────────────

export const MediaProviderKey = z.object({
  tenantId: TenantId,
  provider: z.string(),
  /** 引用租户密钥 */
  secretId: SecretId,
  enabled: z.boolean(),
  createdAt: Timestamp,
});
export type MediaProviderKey = z.infer<typeof MediaProviderKey>;

export const TemplateCapabilities = z.object({ templateId: TemplateId, capabilities: z.array(CapabilityRef) });
